import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import {
  ArrowRight,
  ArrowUpRight,
  Sprout,
  Camera,
  Radar,
  Trees,
  ShieldCheck,
  Sparkles,
  CircleCheck,
  Leaf,
  Boxes,
} from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { COMPANY } from "../lib/constants";

const METHOD_BENEFITS = {
  photogrammetry: [
    "Centimetre-grade orthomosaics from RGB aerial capture",
    "True-colour reference imagery for whole-field documentation",
    "Surface models and 3D meshes for visual context",
    "Fast turnaround and cost-effective for whole-field coverage",
  ],
  lidar: [
    "True bare-earth elevation under crop canopy",
    "Drainage, contour, and tile design ready outputs",
    "Crop height & density modelling from canopy returns",
    "Survey-grade vertical accuracy, day or night, any lighting",
  ],
};

const ADD_ON_SERVICES = [
  {
    icon: "leaf",
    title: "Multispectral imagery",
    desc: "NDVI, crop health analytics, and patch-level performance analysis from dedicated multispectral sensor capture — added to any photogrammetry or LiDAR mission.",
  },
  {
    icon: "boxes",
    title: "3D models of farming infrastructure",
    desc: "Photoreal scans and meshes of bins, sheds, fences, equipment, and on-farm structures for insurance records, asset tracking, and operational planning.",
  },
];

const COST_DRIVERS = [
  {
    icon: Trees,
    title: "Bare-earth under canopy",
    desc: "Laser pulses penetrate crop canopy to expose true ground topography — the foundation for drainage, tile, and grading work that photogrammetry simply cannot deliver.",
  },
  {
    icon: ShieldCheck,
    title: "Drainage & contour ready",
    desc: "Cm-grade vertical accuracy makes LiDAR data drop straight into drainage software, surface-flow models, and earthworks design pipelines — no extra ground survey required.",
  },
  {
    icon: Sprout,
    title: "Crop height & density",
    desc: "Canopy returns separate top-of-crop from ground, letting agronomists quantify field-by-field biomass, height variance, and growth-stage performance directly from the point cloud.",
  },
];

const APPLICATIONS = [
  {
    title: "Drainage & tile planning",
    desc: "Bare-earth contours and slope rasters feed directly into surface drainage design and tile layout software.",
  },
  {
    title: "Crop density & height",
    desc: "Canopy returns from LiDAR — or NDVI from multispectral capture — quantify field-by-field performance.",
  },
  {
    title: "Field topography",
    desc: "Decimetre-accurate elevation models for grading, levelling, and pivot path planning.",
  },
  {
    title: "Volumetric analysis",
    desc: "Pile and bin volumes, trench excavation, and earthwork tracking captured aerially in one pass.",
  },
];

export default function TbirdAg() {
  return (
    <div className="bg-black min-h-screen text-white" data-testid="tbirdag-page">
      <SEO
        title="Agricultural Aerial Mapping & LiDAR Manitoba | Thunderbird Geospatial Inc."
        description="Aerial LiDAR and photogrammetry purpose-built for agriculture across Manitoba and Saskatchewan — drainage, tile design, crop modelling, and full-field topography."
        canonical="https://tbirdgeo.com/agriculture/"
      />
      <Navbar />

      {/* Hero */}
      <section
        data-testid="tbirdag-hero"
        className="relative min-h-[88vh] overflow-hidden bg-black tb-noise"
      >
        <motion.div
          initial={{ scale: 1.08, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.2, 0.8, 0.2, 1] }}
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1920&q=80')",
            filter: "saturate(0.9) contrast(1.05)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/55 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.55)_70%,#000_100%)]" />
        <div className="absolute inset-0 tb-grid-overlay mix-blend-overlay opacity-70" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-20 min-h-[88vh] flex flex-col justify-center">
          <motion.img
            src="https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/y91psv1c_FullLogo_Transparent_NoBuffer%20%2813%29.png"
            alt="TbirdAg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="h-20 md:h-32 w-auto object-contain self-start mb-5"
            draggable="false"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center self-start gap-2 rounded-full border border-[#3DC0AF]/40 bg-[#3DC0AF]/10 px-3 py-1 text-[11px] tracking-[0.2em] uppercase text-[#3DC0AF] font-mono"
          >
            <Sparkles className="h-3 w-3" />
            TbirdAg — Precision Agriculture by Thunderbird
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-8 max-w-5xl text-5xl sm:text-6xl lg:text-7xl font-display font-black tracking-tighter leading-[0.95]"
          >
            Ground-truth data{" "}
            <span className="italic font-semibold text-zinc-400">for</span>{" "}
            <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-white via-white to-[#3DC0AF] bg-clip-text text-transparent">
              the farms that feed Canada.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-6 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed"
          >
            Aerial LiDAR and photogrammetry purpose-built for agriculture -
            terrain data for drainage, crop density mapping, and field-grade
            elevation models delivered to your means.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#pricing"
              data-testid="tbirdag-cta-pricing"
              className="group inline-flex items-center gap-3 rounded-full bg-[#3DC0AF] hover:bg-[#00BA8B] text-white px-7 py-4 text-sm font-medium transition-colors"
            >
              See pricing
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <Link
              to="/#quote"
              data-testid="tbirdag-cta-quote"
              className="group inline-flex items-center gap-3 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/5 text-white px-7 py-4 text-sm font-medium transition-colors"
            >
              <Sprout className="h-4 w-4" />
              Request a quote
            </Link>
          </motion.div>
        </div>

        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-black" />
      </section>

      {/* Two methods */}
      <section
        data-testid="tbirdag-methods"
        className="relative py-24 md:py-32 bg-black"
      >
        <div className="absolute inset-0 tb-grid-dense opacity-30 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#3DC0AF] font-mono font-bold">
                / TWO METHODS, ONE PIPELINE
              </div>
              <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter leading-[0.95] max-w-3xl">
                Photogrammetry vs LiDAR.
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md">
              Two acquisition methods we deploy depending on the deliverable
              you need — often used together on the same flight day.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Photogrammetry */}
            <motion.div
              data-testid="method-photogrammetry"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-7 md:p-9"
            >
              {/* Background blend — RGB farm ortho */}
              <img
                src="https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/pt6crjw6_farm_ortho_sample.png"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-90"
                style={{
                  WebkitMaskImage:
                    "radial-gradient(ellipse 80% 80% at 50% 50%, rgba(0,0,0,1) 30%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0) 100%)",
                  maskImage:
                    "radial-gradient(ellipse 80% 80% at 50% 50%, rgba(0,0,0,1) 30%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0) 100%)",
                  mixBlendMode: "screen",
                }}
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/85" />
              <div className="relative flex items-start justify-between">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-black/40 text-[#5EE3CE]">
                  <Camera className="h-6 w-6" />
                </span>
                <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-400">
                  RGB Aerial
                </span>
              </div>
              <h3 className="relative mt-8 text-2xl md:text-3xl font-display font-bold tracking-tight">
                Photogrammetry
              </h3>
              <p className="relative mt-3 text-sm text-zinc-300 leading-relaxed">
                Stitched aerial imagery and surface data captured from RGB
                cameras. Ideal for visual reference, whole-field documentation,
                and broad-coverage operations.
              </p>
              <ul className="relative mt-6 space-y-3">
                {METHOD_BENEFITS.photogrammetry.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-sm text-zinc-200"
                  >
                    <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#3DC0AF]" />
                    {b}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* LiDAR — featured */}
            <motion.div
              data-testid="method-lidar"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative overflow-hidden rounded-2xl border border-[#3DC0AF]/45 bg-gradient-to-br from-[#3DC0AF]/[0.18] via-[#0b1812] to-black p-7 md:p-9 tb-glow"
            >
              {/* Background blend — LiDAR DEM sample */}
              <img
                src="https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/c8aqute4_lidarsampel.jpg"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-95"
                style={{
                  WebkitMaskImage:
                    "radial-gradient(ellipse 80% 80% at 50% 50%, rgba(0,0,0,1) 30%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0) 100%)",
                  maskImage:
                    "radial-gradient(ellipse 80% 80% at 50% 50%, rgba(0,0,0,1) 30%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0) 100%)",
                  mixBlendMode: "screen",
                }}
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-black/55 to-black/85" />
              <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#3DC0AF]/25 blur-3xl" />
              <div className="pointer-events-none absolute inset-0 tb-grid-dense opacity-[0.18]" />
              <div className="relative flex items-start justify-between">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-[#3DC0AF]/60 bg-[#3DC0AF]/15 text-[#5EE3CE]">
                  <Radar className="h-6 w-6" />
                </span>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#3DC0AF]/50 bg-black/60 backdrop-blur px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-mono text-[#3DC0AF]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#3DC0AF] animate-pulse" />
                    Higher Value
                  </span>
                  <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-400">
                    Active Sensor
                  </span>
                </div>
              </div>
              <h3 className="relative mt-8 text-2xl md:text-3xl font-display font-bold tracking-tight">
                LiDAR
              </h3>
              <p className="relative mt-3 text-sm text-zinc-300 leading-relaxed">
                Active laser ranging delivers true ground elevation under
                crop canopy. The foundation for drainage, tile, contour, and
                volumetric work.
              </p>
              <ul className="relative mt-6 space-y-3">
                {METHOD_BENEFITS.lidar.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-sm text-zinc-200"
                  >
                    <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#3DC0AF]" />
                    {b}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Pricing band */}
          <div
            id="pricing"
            data-testid="tbirdag-pricing"
            className="mt-12 rounded-2xl border border-white/10 bg-black/40 p-6 md:p-8"
          >
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#3DC0AF] font-mono font-bold">
              / Field Mapping Services
            </div>
            <h3 className="mt-3 text-2xl md:text-3xl font-display font-bold tracking-tight">
              Manitoba &amp; Saskatchewan
            </h3>
            <p className="mt-3 text-zinc-300 text-sm md:text-base max-w-2xl leading-relaxed">
              Custom pricing based on acreage, terrain, and data requirements.
              Volume programs and multi-flight subscriptions are available for
              large-scale operations.
            </p>
            <p className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-mono text-[#3DC0AF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#3DC0AF]" />
              Minimum project fee applies to all services
            </p>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 border-t border-white/10 pt-5">
              <ul className="space-y-1.5 text-sm text-zinc-200">
                <li className="flex items-center gap-2">
                  <span className="text-[#3DC0AF]">▸</span>
                  Photogrammetry &amp; LiDAR available
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#3DC0AF]">▸</span>
                  Contact us for a tailored quote
                </li>
              </ul>
              <Link
                to="/#quote"
                data-testid="tbirdag-pricing-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[#3DC0AF] hover:bg-[#5EE3CE] text-black font-semibold px-5 py-2.5 text-sm transition shadow-lg shadow-[#3DC0AF]/20"
              >
                Request a tailored quote
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Add-on / optional services */}
      <section
        data-testid="tbirdag-addons"
        className="relative py-20 md:py-28 bg-black"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#3DC0AF] font-mono font-bold">
                / ADDITIONAL OPTIONS
              </div>
              <h2 className="mt-4 text-4xl sm:text-5xl font-display font-black tracking-tighter leading-[0.95] max-w-3xl">
                Available as add-ons.
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md">
              Add-on capabilities priced per project — quoted alongside your
              photogrammetry or LiDAR engagement.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-5">
            {ADD_ON_SERVICES.map((s, i) => {
              const Icon = s.icon === "leaf" ? Leaf : Boxes;
              return (
                <motion.div
                  key={s.title}
                  data-testid={`tbirdag-addon-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: i * 0.08 }}
                  className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-7 md:p-9 hover:border-[#3DC0AF]/40 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-black/40 text-[#5EE3CE]">
                      <Icon className="h-6 w-6" />
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-300">
                      Quote on request
                    </span>
                  </div>
                  <h3 className="mt-7 text-xl md:text-2xl font-display font-bold tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                    {s.desc}
                  </p>
                  <Link
                    to="/#quote"
                    data-testid={`tbirdag-addon-${i}-cta`}
                    className="mt-6 inline-flex items-center gap-2 text-sm text-[#3DC0AF] hover:text-[#5EE3CE]"
                  >
                    Request pricing
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why LiDAR costs more */}
      <section
        data-testid="tbirdag-why"
        className="relative py-24 md:py-32 bg-[#05070F] overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(61,192,175,0.12),transparent_60%)]" />
          <div className="absolute inset-0 tb-grid-dense opacity-30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#3DC0AF] font-mono font-bold">
                / THE LIDAR VALUE PREMIUM
              </div>
              <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter leading-[0.95] max-w-3xl">
                Datasets only LiDAR can deliver.
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md">
              Three concrete reasons LiDAR delivers more value than a
              photogrammetry-only flight.
            </p>
          </div>

          {/* Cards with layered dataset visual blended behind */}
          <div className="relative isolate py-8 md:py-12">
            <motion.img
              src="https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/ocmcikd0_ChatGPT%20Image%20Apr%2028%2C%202026%2C%2010_31_43%20PM.png"
              alt=""
              aria-hidden="true"
              draggable="false"
              initial={{ opacity: 0, scale: 1.04 }}
              whileInView={{ opacity: 0.45, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              data-testid="tbirdag-layered-visual"
              className="absolute inset-0 w-full h-full object-contain object-center select-none pointer-events-none -z-10"
              style={{
                WebkitMaskImage:
                  "radial-gradient(ellipse 70% 75% at 50% 50%, rgba(0,0,0,1) 25%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0) 92%)",
                maskImage:
                  "radial-gradient(ellipse 70% 75% at 50% 50%, rgba(0,0,0,1) 25%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0) 92%)",
                filter: "saturate(0.85)",
              }}
            />

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {COST_DRIVERS.map((d, i) => {
                const Icon = d.icon;
                return (
                  <motion.div
                    key={d.title}
                    data-testid={`cost-driver-${i}`}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: (i % 6) * 0.06 }}
                    className="rounded-2xl border border-white/10 bg-[#05070F]/85 backdrop-blur-md p-7 hover:border-[#3DC0AF]/40 transition-colors"
                  >
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-black/40 text-[#5EE3CE]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-6 text-xl font-display font-bold tracking-tight">
                      {d.title}
                    </h3>
                    <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                      {d.desc}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section
        data-testid="tbirdag-applications"
        className="relative py-24 md:py-32 bg-black"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#3DC0AF] font-mono font-bold">
              / APPLICATIONS
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl font-display font-black tracking-tighter leading-[0.95]">
              From flight day to field decisions.
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
              Deliverables in formats your agronomy, drainage engineering, or
              precision-ag platform already understands or user friendly outputs.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-[#3DC0AF]/40 bg-[#3DC0AF]/10 px-4 py-2 text-xs font-mono uppercase tracking-[0.2em] text-[#3DC0AF]">
              <Sprout className="h-4 w-4" />
              Sub-brand of Thunderbird Geospatial Inc.
            </div>
          </div>

          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {APPLICATIONS.map((a, i) => (
              <motion.li
                key={a.title}
                data-testid={`tbirdag-app-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-white/25 transition-colors"
              >
                <h3 className="font-display text-lg font-bold tracking-tight">
                  {a.title}
                </h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                  {a.desc}
                </p>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="tbirdag-cta"
        className="relative py-24 bg-[#05070F] overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,rgba(61,192,175,0.18),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
            Get pricing for{" "}
            <span className="text-[#3DC0AF]">your fields.</span>
          </h2>
          <p className="mt-6 text-zinc-400 max-w-xl mx-auto">
            Tell us your acreage, deliverables, and timeline — we'll come back
            with a clear scope and a per-acre quote, usually within one
            business day.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/#quote"
              data-testid="tbirdag-cta-bottom-quote"
              className="group inline-flex items-center gap-3 rounded-full bg-[#3DC0AF] hover:bg-[#00BA8B] text-white px-7 py-4 text-sm font-medium transition-colors"
            >
              Request a TbirdAg quote
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-testid="tbirdag-cta-call"
              className="group inline-flex items-center gap-3 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/5 text-white px-7 py-4 text-sm font-medium transition-colors"
            >
              Call {COMPANY.phone}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
